import { CornerRounding } from '@capsicum/common';
import styled from 'styled-components';

import { useRTL } from '../../../hooks/helpers/useRTL';
import { Reversible } from '../../atoms';
import { Image, type ImageProps } from '../../atoms/image/Image';
import { ImageBackground } from '../image-background/ImageBackground';

export type GridImageShape = ImageProps & {
	url: string;
};

export type ImageGridProps = {
	gridHeight?: number;
	images: Array<GridImageShape>;
	onClick?: () => void;
	optimized?: boolean;
	testID: string;
};

const MAX_GRID_IMAGES = 4;

export const ImageGrid = ({ gridHeight = 280, images, onClick = undefined, optimized, testID }: ImageGridProps) => {
	const { isRTL } = useRTL();
	const getGridAttributes = (index: number) => {
		const { length } = images;

		switch (index) {
			case 0:
				return {
					gridColumn: `1 / span ${length === 1 ? 2 : 1}`,
					gridRow: `1 / span ${length <= 3 ? 2 : 1}`
				};
			case 1:
				return {
					gridColumn: '2',
					gridRow: `1 / span ${length === 2 ? 2 : 1}`
				};
			case 2:
				return {
					gridColumn: '2',
					gridRow: '2'
				};
			case 3:
				return {
					gridColumn: '2',
					gridRow: '2'
				};
			default:
				return {
					gridColumn: 'auto',
					gridRow: 'auto'
				};
		}
	};

	const isExtaImagesLabelVisible = (index: number) =>
		images.length > MAX_GRID_IMAGES && index === MAX_GRID_IMAGES - 1;

	const renderImageWithExtraLabel = (image: GridImageShape) => {
		const text = `+${images.length - MAX_GRID_IMAGES}`;

		return (
			<ImageBackground imageStyle={{ height: '100%', zIndex: '0' }} src={image.url}>
				<S.ExtraImagesLabelContainer $isRTL={isRTL}>
					<Reversible>
						<Image alt={`+${text} images`} fill optimized={optimized} src={CornerRounding} />
					</Reversible>
					<S.ExtraImagesLabelText $isRTL={isRTL}>{text}</S.ExtraImagesLabelText>
				</S.ExtraImagesLabelContainer>
			</ImageBackground>
		);
	};

	if (!images.length) return null;

	return (
		<S.Container
			$gridHeight={gridHeight}
			$hasClickEvent={typeof onClick === 'function'}
			data-testid={testID}
			onClick={onClick}
		>
			{images.slice(0, MAX_GRID_IMAGES).map((image, index) => (
				<S.ImageWrapper
					key={index}
					style={{
						gridArea: getGridAttributes(index).gridColumn,
						gridRow: getGridAttributes(index).gridRow
					}}
				>
					{!isExtaImagesLabelVisible(index) ? (
						<Image alt="grid image" fill optimized={optimized} src={image.url} />
					) : (
						renderImageWithExtraLabel(image)
					)}
				</S.ImageWrapper>
			))}
		</S.Container>
	);
};
namespace S {
	export const Container = styled.div<{ $gridHeight: number; $hasClickEvent: boolean }>`
		display: grid;
		grid-auto-flow: row dense;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: ${({ $gridHeight }) => `${$gridHeight / 2}px ${$gridHeight / 2}px`};
		gap: ${({ theme }) => theme.spacings.xxsmall};
		align-content: space-around;
		position: relative;
		width: 100%;
		cursor: ${({ $hasClickEvent }) => ($hasClickEvent ? 'pointer' : 'auto')};
	`;

	export const ExtraImagesLabelContainer = styled.div<{ $isRTL: boolean }>`
		position: absolute;
		top: 0;
		${({ $isRTL }) => ($isRTL ? 'left: 0' : 'right: 0')};
		bottom: 0;
	`;

	export const ExtraImagesLabelText = styled.span<{ $isRTL: boolean }>`
		color: ${({ theme }) => theme.colors.white};
		${({ theme }) => theme.fonts.headings.display2};
		position: absolute;
		bottom: 7px;
		${({ $isRTL }) => ($isRTL ? 'left: 10px' : 'right: 10px')};
	`;

	export const ImageWrapper = styled.div`
		position: relative;
	`;

	export const ExtraImagesLabel = styled.div`
		position: relative;
		z-index: 1;
	`;
}
